import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  fetchAudioForPlayingEffect,
  fetchWaveformFeatureFlag,
} from 'app/store/AudioPlayer/effects';
import {
  AudioPlayerAudioInstance,
  LoadAudioAction,
  PlayMode,
} from 'app/store/AudioPlayer/slice.types';
import { Link } from 'types/api.types';
import { GenericObject } from 'types/generic.types';
import { getAudioQueueIndex } from 'app/store/AudioPlayer/slice.utils';
import {
  deletePlaylistAudioEffect,
  deletePlaylistImageStatusEffect,
  fetchPlaylistImageStatusEffect,
  movePlaylistAudioEffect,
} from 'app/store/Playlists/effects';
import { PlaylistImage } from 'types/playlists.types';
import { ImageStatusType } from 'types/enums';
import { fetchSharedAudioSrcEffect } from '../Shares/effects';

export interface PlaylistDetails {
  guid: string;
  title: string;
  description: string;
  author?: string;
}

export interface PlaylistLoadingState {
  loadingPlaylist?: boolean;
  loadingPlaylistGuid?: string;
  images?: PlaylistImage[];
}

export interface AudioPlayerState {
  queue: AudioPlayerAudioInstance[];
  queueIndex: number;
  playlistDetails?: PlaylistDetails;
  playlistLoadingState?: PlaylistLoadingState;
  images?: PlaylistImage[];
  playMode: PlayMode;
  isPlayerReady: boolean;
  isOpen: boolean;
  isPlaying: boolean;
  isWaveformFeatureEnabled: boolean;
  isWaveformError: boolean;
  isWaveformOpen: boolean;
  repeat: boolean;
  extended: boolean;
  playOnInitialLoad: boolean;
  _links?: GenericObject<Link>;
}

export const audioPlayerInitialState: AudioPlayerState = {
  queue: [],
  queueIndex: 0,
  isOpen: false,
  isPlaying: false,
  isWaveformFeatureEnabled: false,
  isWaveformError: false,
  isWaveformOpen: true,
  playMode: 'single',
  isPlayerReady: false,
  playOnInitialLoad: false,
  repeat: false,
  extended: false,
};

const slice = createSlice({
  name: 'audioPlayer',
  initialState: audioPlayerInitialState,
  reducers: {
    loadAudioAction(state, { payload }: PayloadAction<LoadAudioAction>) {
      const {
        audio,
        playIndex = 0,
        playMode = 'single',
        playlistDetails,
        playOnInitialLoad = true,
      } = payload;
      state.queue = audio;
      state.queueIndex = playIndex;
      state.playlistDetails = playlistDetails;
      state.playMode = playMode;
      state.playOnInitialLoad = playOnInitialLoad;

      state.isPlaying = false;
      state.isOpen = true;
      state._links = payload._links;
    },
    addAudioListToQueueAction(
      state,
      { payload }: PayloadAction<AudioPlayerAudioInstance[]>
    ) {
      state.queue = [...payload, ...state.queue];
      state.queueIndex += payload.length;
    },
    setIsPlayingAction(state, { payload }: PayloadAction<boolean>) {
      state.isPlaying = payload;
      if (!state.isOpen && payload) {
        state.isOpen = true;
      }
    },
    setIsPlayerReadyAction(state, { payload }: PayloadAction<boolean>) {
      state.isPlayerReady = payload;
    },
    closePlayerAction(state) {
      state.queue = [];
      state.queueIndex = 0;
      state.playlistDetails = undefined;
      state.isPlaying = false;
      state.isOpen = false;
      state.playlistLoadingState = undefined;
      state.playOnInitialLoad = false;
    },
    setIsWaveformError(state, { payload }: PayloadAction<boolean>) {
      state.isWaveformError = payload;
    },
    setIsWaveformOpen(state, { payload }: PayloadAction<boolean>) {
      state.isWaveformOpen = payload;
    },
    setRepeatAction(state, { payload }: PayloadAction<boolean>) {
      state.repeat = payload;
    },
    setExtendedAction(state, { payload }: PayloadAction<boolean>) {
      state.extended = payload;
    },
    skipToNextAudioAction(state) {
      if (state.queue.length - 1 > state.queueIndex) {
        state.queueIndex += 1;
        state.isPlaying = true;
      } else {
        state.isPlaying = false;
      }
    },
    skipBackToPreviousAudioAction(state) {
      if (state.queueIndex > 0) {
        state.queueIndex -= 1;
        state.isPlaying = true;
      }
    },
    setQueueIndexAction(state, { payload }: PayloadAction<number>) {
      state.queueIndex = payload;
      state.isPlaying = true;
    },

    setPlayOnInitialLoadAction(state, { payload }: PayloadAction<boolean>) {
      state.playOnInitialLoad = payload;
    },
    resetCurrentAudioErrorStateAction(state) {
      if (state.queue[state.queueIndex]) {
        state.queue[state.queueIndex] = {
          ...state.queue[state.queueIndex],
          isError: false,
        };
      }
    },
    setPlaylistLoadingStateAction(
      state,
      { payload }: PayloadAction<PlaylistLoadingState>
    ) {
      state.playlistLoadingState = {
        ...state.playlistLoadingState,
        ...payload,
      };
      state.images = payload.images;
    },
    togglePlayerOpenAction(state, { payload }: PayloadAction<boolean>) {
      state.isOpen = payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWaveformFeatureFlag.fulfilled, (state, { payload }) => {
        state.isWaveformFeatureEnabled = payload;
        if (!payload) {
          state.isWaveformOpen = false;
        }
      })
      .addCase(
        fetchAudioForPlayingEffect.pending,
        (state, { meta: { arg } }) => {
          const audioQueueIndex = getAudioQueueIndex({
            queue: state.queue,
            audioGuid: arg.audioGuid,
            playlistAudioGuid: arg?.playlistAudioGuid,
          });

          if (audioQueueIndex < 0) {
            return;
          }

          state.queue[audioQueueIndex] = {
            ...state.queue[audioQueueIndex],
            loading: true,
            isError: false,
          };

          state.isPlayerReady = false;
        }
      )
      .addCase(fetchAudioForPlayingEffect.fulfilled, (state, { payload }) => {
        const fetchedAudioQueueIndex = getAudioQueueIndex({
          queue: state.queue,
          audioGuid: payload?.audioGuid || '',
          playlistAudioGuid: payload?.playlistAudioGuid,
        });

        if (fetchedAudioQueueIndex < 0 || !payload) {
          return;
        }
        state.queue[fetchedAudioQueueIndex] = {
          ...state.queue[fetchedAudioQueueIndex],
          ...payload,
          loading: false,
        };
      })
      .addCase(
        fetchAudioForPlayingEffect.rejected,
        (state, { meta: { arg } }) => {
          const audioQueueIndex = getAudioQueueIndex({
            queue: state.queue,
            audioGuid: arg.audioGuid,
            playlistAudioGuid: arg.playlistAudioGuid,
          });

          if (audioQueueIndex < 0) {
            return;
          }

          state.queue[audioQueueIndex] = {
            ...state.queue[audioQueueIndex],
            isError: true,
            loading: false,
          };
          state.isPlayerReady = true;
          state.isPlaying = false;
        }
      )
      .addCase(
        fetchSharedAudioSrcEffect.fulfilled,
        (state, { payload: { audioSrc, waveformSrc }, meta: { arg } }) => {
          const audioQueueIndex = getAudioQueueIndex({
            queue: state.queue,
            audioGuid: arg.audioGuid,
            playlistAudioGuid: arg.playlistAudioGuid,
          });

          if (audioQueueIndex < 0) {
            return;
          }

          state.queue[audioQueueIndex] = {
            ...state.queue[audioQueueIndex],
            audioSrc,
            waveformSrc,
            loading: false,
            isError: false,
          };
        }
      )
      .addCase(
        fetchSharedAudioSrcEffect.pending,
        (state, { meta: { arg } }) => {
          const audioQueueIndex = getAudioQueueIndex({
            queue: state.queue,
            audioGuid: arg.audioGuid,
            playlistAudioGuid: arg.playlistAudioGuid,
          });

          if (audioQueueIndex < 0) {
            return;
          }

          state.queue[audioQueueIndex] = {
            ...state.queue[audioQueueIndex],
            loading: true,
            isError: false,
          };

          state.isPlayerReady = false;
        }
      )
      .addCase(
        fetchSharedAudioSrcEffect.rejected,
        (state, { meta: { arg } }) => {
          const audioQueueIndex = getAudioQueueIndex({
            queue: state.queue,
            audioGuid: arg.audioGuid,
            playlistAudioGuid: arg.playlistAudioGuid,
          });

          if (audioQueueIndex < 0) {
            return;
          }

          state.queue[audioQueueIndex] = {
            ...state.queue[audioQueueIndex],
            isError: true,
            loading: false,
          };
          state.isPlayerReady = true;
          state.isPlaying = false;
        }
      )
      .addCase(movePlaylistAudioEffect.fulfilled, (state, { meta }) => {
        const activeQueueGuid =
          state.queue[state.queueIndex]?.playlistAudioGuid;

        if (!activeQueueGuid) {
          return;
        }

        const updatedOrder = meta.arg.reordered
          .map(({ guid }) => {
            return state.queue.find((el) => el.playlistAudioGuid === guid);
          })
          .filter((el): el is Exclude<typeof el, undefined> => Boolean(el));

        const updatedQueueIndex = updatedOrder.findIndex(
          (el) => el.playlistAudioGuid === activeQueueGuid
        );

        state.queue = updatedOrder;
        state.queueIndex = updatedQueueIndex;
      })
      .addCase(deletePlaylistAudioEffect.fulfilled, (state, { meta }) => {
        const activeQueueGuid =
          state.queue[state.queueIndex]?.playlistAudioGuid;
        const isLastAudioPlaying = state.queue.length - 1 === state.queueIndex;

        const filteredQueue = state.queue.filter(
          (el) => el.playlistAudioGuid !== meta.arg.audioGuid
        );
        const updatedQueueIndex = filteredQueue.findIndex(
          (el) => el.playlistAudioGuid === activeQueueGuid
        );

        state.queue = filteredQueue;
        if (isLastAudioPlaying && activeQueueGuid === meta.arg.audioGuid) {
          state.queueIndex = 0;
        } else {
          state.queueIndex =
            updatedQueueIndex === -1 ? state.queueIndex : updatedQueueIndex;
          state.playOnInitialLoad = true;
        }
        if (activeQueueGuid === meta.arg.audioGuid) {
          state.isPlaying = false;
          state.playOnInitialLoad = false;
        }
      })
      .addCase(
        fetchPlaylistImageStatusEffect.fulfilled,
        (state, { payload }) => {
          if (payload.status !== ImageStatusType.SUCCESS) {
            return;
          }
          if (state.playlistDetails?.guid === payload.playlist?.guid) {
            state.images = payload.playlist?.images;
          }
        }
      )
      .addCase(
        deletePlaylistImageStatusEffect.fulfilled,
        (state, { meta: { arg } }) => {
          if (state.playlistDetails?.guid === arg) {
            state.images = [];
          }
        }
      );
  },
});

export const {
  setIsPlayingAction,
  closePlayerAction,
  setIsPlayerReadyAction,
  setIsWaveformError,
  setIsWaveformOpen,
  setRepeatAction,
  setExtendedAction,
  loadAudioAction,
  skipToNextAudioAction,
  skipBackToPreviousAudioAction,
  setQueueIndexAction,
  resetCurrentAudioErrorStateAction,
  setPlayOnInitialLoadAction,
  setPlaylistLoadingStateAction,
  addAudioListToQueueAction,
  togglePlayerOpenAction,
} = slice.actions;

export const audioPlayerReducer = slice.reducer;
